import PropTypes from "prop-types";
import * as React from "react";
import { Product } from "../../domain";
import { useActiveInfluencer } from "../../hooks";
import { useCurrentUser } from "../../globalState";
import * as utils from "../Shop/utils";
import css from "./ProductText.module.css";

const MAX_SIGNERS = 2;

const ProductText = React.memo(function ProductText({
  product,
  isDark,
  isStorefront,
}) {
  const activeInfluencer = useActiveInfluencer();
  const currentUser = useCurrentUser();

  const signers = React.useMemo(() => {
    if (!isDark) {
      const signerText = utils.signerTextFor(product);

      return (
        <p
          className={[css.signerText, css.gradientText].join(" ")}
          title={signerText}
        >
          {signerText}
        </p>
      );
    }

    if (!product.fulfillers.length) {
      return null;
    }

    const fulfillers = product.fulfillers
      .slice(0, MAX_SIGNERS)
      .map(({ name }) => name);

    const remainder = product.fulfillers.length - fulfillers.length;

    if (remainder > 0) {
      fulfillers.push(`and ${remainder} more..`);
    }

    return (
      <p
        className={[css.signerTextDark, css.goldText].join(" ")}
        key="show more"
      >
        Signed by {fulfillers.join(", ")}
      </p>
    );
  }, [isDark, product]);

  const price = React.useMemo(() => {
    if (isStorefront) {
      return product?.subtotal.format();
    }

    if (currentUser.isAdmin || currentUser.isAgent) {
      return product?.price.format();
    }

    return product?.priceFor(activeInfluencer.influencerId).format();
  }, [isStorefront, product, currentUser, activeInfluencer]);

  return (
    <div className={isDark ? css.dark : undefined}>
      <h4 className={css.name} title={product?.name}>
        {product?.name}
      </h4>

      <div className={css.details}>
        {!product?.hasNoSignature && signers}

        {!product.hasNoSignature && (
          <span
            className={[css.price, isDark && css.priceDark]
              .filter(Boolean)
              .join(" ")}
          >
            {price}
          </span>
        )}
      </div>

      {!product.hasNoSignature && (
        <p className={css.type}>{product?.productType}</p>
      )}

      {product.hasNoSignature && (
        <div className={css.noSignature}>
          <p className={css.type}>{product?.productType}</p>
          <p
            className={[css.price, isDark && css.priceDark]
              .filter(Boolean)
              .join(" ")}
          >
            {price}
          </p>
        </div>
      )}
    </div>
  );
});

ProductText.propTypes = {
  product: PropTypes.instanceOf(Product),
  isDark: PropTypes.bool,
  isStorefront: PropTypes.bool,
};

export default ProductText;
